import React, { useState, useRef, useEffect } from 'react'; // useCallback removed
import { useMusicPlayer } from '../context/MusicPlayerContext';

// Icons from the Layout component
const Icon = {
  Play: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M8 5v14l11-7z" /></svg>),
  Pause: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>),
  SkipNext: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M6 6l8.5 6L6 18V6zm9.5 0h2v12h-2V6z" /></svg>),
  SkipPrev: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M18 6v12l-8.5-6L18 6zM5.5 6h2v12h-2z" /></svg>),
  Volume: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M11 5l-5 4H3v6h3l5 4V5z" /><path d="M15 9a4 4 0 010 6" /><path d="M17 7a7 7 0 010 10" /></svg>),
  VolumeMute: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M11 5l-5 4H3v6h3l5 4V5z" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>),
  Repeat: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 014-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 01-4 4H3" /></svg>),
  RepeatOne: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 014-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 01-4 4H3" /><line x1="15" y1="5" x2="11" y2="5" /><line x1="11" y1="5" x2="15" y2="5" /></svg>),
  Shuffle: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><polyline points="16 3 21 8 16 13" /><polyline points="8 21 3 16 8 11" /><line x1="21" y1="8" x2="14" y2="13" /><line x1="12" y1="4" x2="21" y2="13" /></svg>),
  X: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>),
};

const MusicPlayer = () => {
  const { 
    currentSong,
    isPlaying,
    volume,
    currentTime,
    duration,
    isRepeat,
    isShuffle,
    isYouTube,
    togglePlayPause,
    handleNext,
    handlePrevious,
    seekTo,
    setVolume,
    setRepeat,
    setShuffle,
    stop,
    setIsPlaying,
    setCurrentTime,
    setDuration,
  } = useMusicPlayer();

  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [ytPlayer, setYtPlayer] = useState(null);
  const [ytPlayerReady, setYtPlayerReady] = useState(false);
  const [ytMuted, setYtMuted] = useState(false);
  const [youtubeApiLoaded, setYoutubeApiLoaded] = useState(false);
  const playerDivRef = useRef(null);
  const ytPlayerInitialized = useRef(false);

  // Load YouTube IFrame API - ONLY on client side
  useEffect(() => {
    // Check if we're on client side
    if (typeof window === 'undefined') return;

    if (!window.YT) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onload = () => {
        setYoutubeApiLoaded(true);
      };
      script.onerror = () => {
        console.error('Failed to load YouTube API');
      };
      
      // Store reference to clean up
      const scriptElement = document.head.appendChild(script);
      
      return () => {
        if (scriptElement && scriptElement.parentNode) {
          document.head.removeChild(scriptElement);
        }
      };
    } else {
      setYoutubeApiLoaded(true);
    }
  }, []);

  // Create YouTube player ONLY when needed
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Only proceed if we have a YouTube song and API is loaded
    if (!isYouTube || !currentSong?.videoId || !youtubeApiLoaded) {
      return;
    }

    // If we already have a player with the same video, reuse it
    if (ytPlayer && ytPlayerInitialized.current === currentSong.videoId) {
      return;
    }

    // Clean up existing player
    const cleanupExistingPlayer = () => {
      if (ytPlayer) {
        try {
          ytPlayer.destroy();
        } catch (e) {
          // Silent fail on cleanup
        }
        setYtPlayer(null);
        setYtPlayerReady(false);
        ytPlayerInitialized.current = false;
      }
    };

    cleanupExistingPlayer();

    // Create new player
    const createNewPlayer = () => {
      if (!playerDivRef.current || !window.YT || !window.YT.Player) {
        setTimeout(createNewPlayer, 100);
        return;
      }

      try {
        // Clear the container first
        if (playerDivRef.current) {
          playerDivRef.current.innerHTML = '';
        }

        const player = new window.YT.Player(playerDivRef.current, {
          height: '0',
          width: '100%',
          videoId: currentSong.videoId,
          playerVars: {
            autoplay: isPlaying ? 1 : 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
            iv_load_policy: 3,
            cc_load_policy: 0,
            autohide: 1,
          },
          events: {
            onReady: (event) => {
              setYtPlayerReady(true);
              ytPlayerInitialized.current = currentSong.videoId;
              
              // Set volume from context
              event.target.setVolume(volume * 100);
              event.target.unMute();
              setYtMuted(false);
              
              // Play if needed
              if (isPlaying) {
                event.target.playVideo();
              }
            },
            onStateChange: (event) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                setIsPlaying(true);
              } else if (event.data === window.YT.PlayerState.PAUSED) {
                setIsPlaying(false);
              } else if (event.data === window.YT.PlayerState.ENDED) {
                setIsPlaying(false);
                handleNext();
              }
            },
            onError: (event) => {
              console.error('YouTube player error:', event.data);
              setIsPlaying(false);
            }
          }
        });
        
        setYtPlayer(player);
      } catch (error) {
        console.error('Error creating YouTube player:', error);
        setIsPlaying(false);
      }
    };

    // Use a small timeout to ensure DOM is ready
    const timer = setTimeout(createNewPlayer, 50);
    
    return () => {
      clearTimeout(timer);
      cleanupExistingPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSong?.videoId, isYouTube, youtubeApiLoaded]);

  // Sync play/pause with YouTube player
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (isYouTube && ytPlayer && ytPlayerReady) {
      try {
        if (isPlaying) {
          ytPlayer.playVideo();
        } else {
          ytPlayer.pauseVideo();
        }
      } catch (e) {
        // Silent fail
      }
    }
  }, [isPlaying, isYouTube, ytPlayer, ytPlayerReady]);

  // Sync volume with YouTube player
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (isYouTube && ytPlayerReady && ytPlayer) {
      try {
        ytPlayer.setVolume(volume * 100);
        if (volume > 0 && ytMuted) {
          ytPlayer.unMute();
          setYtMuted(false);
        } else if (volume === 0 && !ytMuted) {
          ytPlayer.mute();
          setYtMuted(true);
        }
      } catch (e) {
        // Silent fail
      }
    }
  }, [volume, isYouTube, ytPlayerReady, ytPlayer, ytMuted]);

  // Sync YouTube current time to context
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (isYouTube && ytPlayerReady && ytPlayer) {
      const interval = setInterval(() => {
        try {
          const time = ytPlayer.getCurrentTime();
          if (time > 0) {
            setCurrentTime(time);
          }
          const dur = ytPlayer.getDuration();
          if (dur > 0) {
            setDuration(dur);
          }
        } catch (e) {}
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isYouTube, ytPlayerReady, ytPlayer, setCurrentTime, setDuration]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && ytPlayer) {
        try {
          ytPlayer.destroy();
        } catch (e) {
          // Silent fail on cleanup
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Don't render if no song is playing
  if (!currentSong) return null;

  const handlePlayPause = () => {
    if (isYouTube) {
      if (typeof window === 'undefined') return;
      
      if (ytPlayerReady && ytPlayer) {
        try {
          if (isPlaying) {
            ytPlayer.pauseVideo();
            setIsPlaying(false);
          } else {
            ytPlayer.playVideo();
            setIsPlaying(true);
          }
        } catch (e) {
          // Silent fail
        }
      } else if (!ytPlayer && currentSong?.videoId) {
        // Player not ready yet, just toggle the state
        setIsPlaying(!isPlaying);
      }
    } else {
      togglePlayPause();
    }
  };

  const handleStop = () => {
    if (isYouTube && typeof window !== 'undefined' && ytPlayerReady && ytPlayer) {
      try {
        ytPlayer.stopVideo();
      } catch (e) {
        // Silent fail
      }
    }
    stop();
  };

  const formatTime = (time) => {
    if (isNaN(time) || !time) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    
    if (isYouTube && ytPlayer && ytPlayerReady) {
      try {
        ytPlayer.seekTo(newTime, true);
      } catch (e) {}
    } else {
      seekTo(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <>
      {/* Compact Player */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800 shadow-xl">
        <div className="px-3 sm:px-6 py-2 sm:py-3">
          {/* Progress Bar - Works for both YouTube and local audio */}
          <div
            className="w-full h-1 bg-gray-600 rounded-full cursor-pointer mb-2 sm:mb-3"
            onClick={handleProgressClick}
          >
            <div
              className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-150"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* YouTube Player Container - Always render but conditionally control */}
          <div 
            ref={playerDivRef} 
            className="w-full" 
            style={{ 
              height: isYouTube ? '0px' : '0px', 
              overflow: 'hidden',
              position: 'absolute',
              pointerEvents: 'none',
              opacity: 0 
            }}
          ></div>

          <div className="flex items-center justify-between">
            {/* Song Info */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-3 5-3v6zm7 0l-5-3 5-3v6z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm sm:text-base font-medium text-white truncate">
                  {currentSong.title || 'Unknown Song'}
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 truncate">
                  {currentSong.singer || currentSong.artist || 'Unknown Artist'}
                </p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Shuffle */}
              <button
                onClick={setShuffle}
                className={`p-1.5 sm:p-2 rounded-lg transition-all duration-200 ${isShuffle
                    ? 'text-green-400 bg-green-400/20'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                  }`}
              >
                <Icon.Shuffle className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Previous */}
              <button
                onClick={handlePrevious}
                className="p-1.5 sm:p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-all duration-200"
              >
                <Icon.SkipPrev className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Play/Pause */}
              <button
                onClick={handlePlayPause}
                disabled={isYouTube && !ytPlayerReady}
                className={`p-2 sm:p-3 rounded-full text-white shadow-lg transform hover:scale-105 transition-all duration-200 ${isYouTube && !ytPlayerReady
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-green-500/30'
                  }`}
              >
                {isPlaying ? (
                  <Icon.Pause className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Icon.Play className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>

              {/* Next */}
              <button
                onClick={handleNext}
                className="p-1.5 sm:p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-all duration-200"
              >
                <Icon.SkipNext className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Repeat */}
              <button
                onClick={setRepeat}
                className={`p-1.5 sm:p-2 rounded-lg transition-all duration-200 ${isRepeat
                    ? 'text-green-400 bg-green-400/20'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                  }`}
              >
                <Icon.Repeat className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Volume */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowVolumeSlider(!showVolumeSlider);
                  }}
                  className="p-1.5 sm:p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-all duration-200"
                >
                  {volume === 0 ? (
                    <Icon.VolumeMute className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Icon.Volume className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </button>

                {/* Volume Slider */}
                {showVolumeSlider && (
                  <div className="absolute bottom-full right-0 mb-2 p-2 bg-gray-800 rounded-lg shadow-xl border border-gray-700">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="w-20 h-1 bg-gray-600 rounded-full appearance-none cursor-pointer slider"
                    />
                  </div>
                )}
              </div>

              {/* Close */}
              <button
                onClick={handleStop}
                className="p-1.5 sm:p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-all duration-200"
              >
                <Icon.X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Time Display - Hide for YouTube */}
          {!isYouTube && (
            <div className="flex justify-between items-center mt-1 text-xs text-slate-400">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MusicPlayer;